// Import from libraries
import { type UseSuspenseQueryResult } from '@tanstack/react-query'
import { useState, type TdHTMLAttributes } from 'react'
import { useRouteContext, useLoaderData } from '@tanstack/react-router'
import TableDateCell from '@/components/shared/tables/TableDateCell'
import { BsDownload } from 'react-icons/bs'
import { joinClasses } from '@/utils/styling'

import {
    type GridRowClickEvent,
    type GridColumnProps
} from '@progress/kendo-react-grid';

// Import from scripts
import { FileStatus, type FileSubmission, type FileSubmissionUI } from '@/models/file-submission.tsx'
import { useAuthenticatedSuspenseQuery, fetchAuthenticated } from '@/queries/shared'
import { getTableColumnSelectOptions } from '@/utils/tables'

// Import components
import { DownloadBlob } from '@/components/components'
import Card from '@/components/shared/Card'
import KendoTableClientState from '@/components/shared/tables/KendoTableClientState'
import FileStatusPopup from '@/components/file-submission/FileStatusPopup'
import TooltipTrigger from '@/components/shared/TooltipTrigger';
import Icon from '@/components/shared/Icon';
import { ColumnMenuCheckboxFilterFn } from '@/components/shared/tables/ColumnMenu';
import CategoryFilterCell from '@/components/shared/tables/CategoryFilterCell'
import { DateFilterCell } from '@/components/shared/tables/GridFilterCells'
import { stripAriaSelected } from '@/components/shared/tables/KendoTableClientState/kendo-cell-utils'

const REFETCH_INTERVAL = 10000; // 10 seconds

function getFileStatusName(statusCode: number): string {
    return FileStatus.get(statusCode.toString())?.Text ?? '--';
}

const statusTextColor: Record<string, string> = {
    Posted: "var(--status-success)",
    Failed: "var(--status-error)",
    Rejected: "var(--status-error)",
    Pending: "var(--status-warning)",
    Processing: "var(--status-warning)",
};

export default function SubmittedFilesCard() {
    const leaContext = useRouteContext({ from: '/$leaid' });
    const currentUserOrg = leaContext.currentUserOrg;
    const isStateLevelAdmin = leaContext.isStateLevelAdmin;
    const path = `/json/filesubmissions/${currentUserOrg.esiIdPath}`;
    const { data, error }: UseSuspenseQueryResult<{ records: FileSubmission[] }, Error> = 
        useAuthenticatedSuspenseQuery(path, 'filesubmissions', REFETCH_INTERVAL);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<FileSubmission | null>(null);
    
    const files = data.records ?? [];
    
    // Add StatusName field based on CurrentStatus field
    const tableData: FileSubmissionUI[] = files.map(
        file => ({ ...file, StatusName: getFileStatusName(file.CurrentStatus) })
    );

    const statusOptions = getTableColumnSelectOptions(tableData, 'StatusName');
    const recordTypeOptions = getTableColumnSelectOptions(tableData, 'RecordTypeCode');
    const createdByOptions = getTableColumnSelectOptions(tableData, 'CreatedByUserId');

    // DownloadSourceFileCell hits an endpoint that only exists on the Admin.Web host,
    // so the column must not render when this shared component is served by Public.Web.
    const isAdminWeb = import.meta.env.VITE_ADMIN === '1';

    const columns: GridColumnProps[] = [
        ...(isStateLevelAdmin && isAdminWeb ? [{
            id: 'Download',
            field: 'Download',
            title: 'Download',
            width: 110,
            cells: {
                data: ({ dataItem, tdProps }: { dataItem: FileSubmissionUI, tdProps?: TdHTMLAttributes<HTMLTableCellElement> | null }) =>
                    <DownloadSourceFileCell submission={dataItem} esiIdPath={currentUserOrg.esiIdPath} tdProps={tdProps} />
            },
        }] : []),
        {
            id: 'StatusName',
            field: 'StatusName',
            title: "Status",
            cells: {
                data: ({ dataItem, tdProps }) => SubmissionStatusCell(dataItem as FileSubmissionUI, tdProps),
                filterCell: (props) => CategoryFilterCell(props, [...statusOptions]) 
            },
        },
        {
            id: 'ClientFileName',
            field: 'ClientFileName',
            title: "Filename",
            width: 250
        },
        {
            id: 'RecordTypeCode',
            field: 'RecordTypeCode',
            title: "Record Type",
            cells: {
                filterCell: (props) => CategoryFilterCell(props, [...recordTypeOptions]) 
            },
        },
        {
            id: 'CreateDate',
            field: 'CreateDate',
            title: "Date Submitted",
            filter: "date",
            cells: {
                data: TableDateCell,
                filterCell: (props) => DateFilterCell(props, "Date Submitted")
            }
        },
        {
            id: 'CreatedByUserId',
            field: 'CreatedByUserId',
            title: "Submitted By",
            cells: {
                filterCell: (props) => CategoryFilterCell(props, [...createdByOptions]) 
            },
            width: 250
        },
        {
            id: 'Errors',
            field: 'Errors',
            title: "# of Errors",
            filter: "numeric"
        },
        {
            id: 'InputRecords',
            field: 'InputRecords',
            title: "# of Records",
            filter: "numeric"
        },
    ]

    const handleKendoRowClick = (event: GridRowClickEvent) => {
        const file: FileSubmissionUI = event.dataItem as FileSubmissionUI,
              status = file.StatusName;

        // Only open popup for Posted, Failed, and Rejected files (not pending)
        if (status === "Posted" || 
            status === "Failed" || 
            status === "Rejected") 
        {
            setSelectedFile(file);
            setModalOpen(true);
        }
    }

    const handleModalClose = () => {
        setSelectedFile(null);
        setModalOpen(false);
    }

    return  (
        <>
            <Card className="mb-3 no-padding card-border">
                <div
                    className="text-white p-3 d-flex align-items-center gap-2"
                    style={{
                        background: "var(--color-p3)",
                        fontWeight: 500,
                        borderTopLeftRadius: "inherit",
                        borderTopRightRadius: "inherit",
                    }}
                >
                    <span>Submitted Files</span>
                </div>

                <KendoTableClientState
                    data={tableData}
                    columns={columns}
                    error={error}
                    initialSort={[{ field: 'CreateDate', dir: 'desc' }]}
                    onRowClick={handleKendoRowClick}
                />
            </Card>

            {selectedFile && 
                <FileStatusPopup
                    show={modalOpen}
                    onClose={handleModalClose}
                    file={selectedFile}
                />
            }
        </>
    )
}

function DownloadSourceFileCell({ submission, esiIdPath, tdProps }: { submission: FileSubmissionUI, esiIdPath: string, tdProps?: TdHTMLAttributes<HTMLTableCellElement> | null }) {
    const globalState = useLoaderData({ from: '__root__' });
    const cleanTdProps = stripAriaSelected(tdProps);

    const downloadSourceFile = async (fileSubmissionId: string) => {
        const response = await fetchAuthenticated(
            globalState,
            `/json/filesubmission/${esiIdPath}/${fileSubmissionId}/sourcefile`,
            { method: 'GET' }
        );
        const contentDisposition = response.headers.get('Content-Disposition');
        const serverFilename = contentDisposition?.match(/filename="?([^";]+)"?/)?.[1];
        DownloadBlob(await response.blob(), serverFilename || `${submission.RecordTypeCode}-${submission.Id}.csv`);
    };

    return <td
        {...cleanTdProps}
        className={joinClasses("text-center", cleanTdProps?.className)}
        style={{ ...cleanTdProps?.style, verticalAlign: 'middle' }}
    >
        <button
            type="button"
            className="btn btn-link p-0"
            title="Download original file"
            aria-label={`Download original file for ${submission.ClientFileName ?? "file"}`}
            onClick={(e) => {
                e.stopPropagation();
                downloadSourceFile(submission.Id).catch((error) =>
                    console.error("Error downloading source file:", error)
                );
            }}
        >
            <BsDownload size={18} aria-hidden="true" />
        </button>
    </td>
}

function SubmissionStatusCell(submission: FileSubmissionUI, tdProps?: TdHTMLAttributes<HTMLTableCellElement> | null) {
    const statusId = submission.CurrentStatus;
    const status = FileStatus.get(statusId.toString());
    const statusText = status?.Text ?? '';
    const color = statusTextColor[statusText] ?? "var(--status-default)";
    const errorCount: number = submission.Errors;

    const label = (
        <span
            className="d-flex align-items-center gap-1"
            style={{ color, whiteSpace: "nowrap" }}
        >
            {status?.Icon && <Icon name={status.Icon} />}
            {statusText}
        </span>
    );

    return <td {...stripAriaSelected(tdProps)}>
        {statusText === 'Rejected' ?
            <TooltipTrigger tooltipText={`${errorCount ?? 0} errors`}>
                {label}
            </TooltipTrigger> :
            label
        }
    </td>
}